import { useState } from 'react';
import { Button, Chip, IconButton, Stack, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import { type GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { PageHeader } from '../../../shared/components/PageHeader';
import { DataTable, type DataTableQuery } from '../../../shared/components/DataTable';
import { GRID_PAGE_SIZE, usePersistedGridState } from '../../../shared/grid/usePersistedGridState';
import { IncludeInactiveToggle } from '../../../shared/components/IncludeInactiveToggle';
import { ConfirmDialog } from '../../../shared/components/ConfirmDialog';
import { ErrorState } from '../../../shared/components/ErrorState';
import { useToast } from '../../../shared/stores/toast.store';
import { describeApiError } from '../../../shared/api/errors';
import { companiesHooks } from '../hooks';
import { companiesApi } from '../api';
import { CompanyFormDialog } from '../CompanyFormDialog';
import { CompanyAddressesDialog } from '../addresses/CompanyAddressesDialog';
import { formatCnpj } from '../../../shared/utils/document';
import type { CompanyDto, CompanyListItem } from '../types';

const DEFAULT_QUERY: DataTableQuery = {
  page: 1,
  pageSize: GRID_PAGE_SIZE,
  sortBy: 'nome',
  sortOrder: 'asc',
};

export function CompaniesListPage(): JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const { query, setQuery, clear } = usePersistedGridState('empresas', DEFAULT_QUERY, {});
  const [editing, setEditing] = useState<CompanyDto | null | 'new'>(null);
  const [pendingDelete, setPendingDelete] = useState<CompanyListItem | null>(null);
  const [addressesFor, setAddressesFor] = useState<CompanyListItem | null>(null);

  const listQuery = companiesHooks.useList(query);
  const removeMutation = companiesHooks.useDelete();

  async function openEdit(row: CompanyListItem): Promise<void> {
    try {
      setEditing(await companiesApi.get(row.id));
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  async function confirmDelete(): Promise<void> {
    if (!pendingDelete) return;
    try {
      await removeMutation.mutateAsync(pendingDelete.id);
      toast.success(t('features.empresa.toasts.deleted'));
      setPendingDelete(null);
    } catch (err) {
      toast.error(describeApiError(err));
    }
  }

  const columns: GridColDef<CompanyListItem>[] = [
    { field: 'nome', headerName: t('features.empresa.columns.nome'), flex: 1, minWidth: 220 },
    {
      field: 'documento',
      headerName: t('features.empresa.columns.documento'),
      width: 180,
      valueFormatter: (value: string) => formatCnpj(value),
    },
    {
      field: 'telefone',
      headerName: t('features.empresa.columns.telefone'),
      width: 150,
      sortable: false,
    },
    {
      field: 'ativo',
      headerName: t('features.empresa.columns.ativo'),
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={
            params.value
              ? t('features.empresa.status.active')
              : t('features.empresa.status.inactive')
          }
          color={params.value ? 'success' : 'default'}
          size="small"
          variant={params.value ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      field: 'criadoPorNome',
      headerName: t('features.empresa.columns.criadoPorNome'),
      width: 170,
      sortable: false,
    },
    {
      field: 'atualizadoEm',
      headerName: t('features.empresa.columns.atualizadoEm'),
      width: 170,
      valueFormatter: (value: string | null) =>
        value ? dayjs(value).format('DD/MM/YYYY HH:mm') : '—',
    },
    {
      field: 'actions',
      headerName: '',
      width: 140,
      sortable: false,
      filterable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title={t('features.empresa.actions.manageAddresses')}>
            <IconButton
              size="small"
              aria-label={t('features.empresa.actions.manageAddressesAria', {
                nome: params.row.nome,
              })}
              onClick={() => setAddressesFor(params.row)}
            >
              <HomeWorkIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('common.edit')}>
            <IconButton
              size="small"
              aria-label={t('features.empresa.actions.edit', { nome: params.row.nome })}
              onClick={() => void openEdit(params.row)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('common.delete')}>
            <IconButton
              size="small"
              color="error"
              aria-label={t('features.empresa.actions.delete', { nome: params.row.nome })}
              onClick={() => setPendingDelete(params.row)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  if (listQuery.isLoadingError) {
    return (
      <>
        <PageHeader title={t('menu.companyRegistry')} />
        <ErrorState
          description={describeApiError(listQuery.error)}
          onRetry={() => listQuery.refetch()}
        />
      </>
    );
  }

  // Cadastro em tela cheia dentro da área de conteúdo (substitui a lista; o
  // estado da lista — query/filtros — é preservado pois a página segue montada).
  if (editing !== null) {
    return (
      <CompanyFormDialog
        open
        company={editing === 'new' ? null : editing}
        onClose={() => setEditing(null)}
      />
    );
  }

  return (
    <>
      <PageHeader
        title={t('menu.companyRegistry')}
        breadcrumbs={[{ label: t('menu.company') }, { label: t('menu.companyRegistry') }]}
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing('new')}>
            {t('features.empresa.actions.new')}
          </Button>
        }
      />

      <DataTable<CompanyListItem>
        rows={listQuery.data?.data ?? []}
        columns={columns}
        total={listQuery.data?.meta.total ?? 0}
        loading={listQuery.isLoading || listQuery.isFetching}
        query={query}
        onQueryChange={setQuery}
        onClear={clear}
        searchPlaceholder={t('features.empresa.searchPlaceholder')}
        filters={<IncludeInactiveToggle query={query} onChange={setQuery} />}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        title={t('features.empresa.deleteConfirm.title')}
        description={t('features.empresa.deleteConfirm.description', {
          nome: pendingDelete?.nome ?? '',
        })}
        confirmLabel={t('common.delete')}
        loading={removeMutation.isPending}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />

      <CompanyAddressesDialog
        open={addressesFor !== null}
        companyId={addressesFor?.id ?? null}
        companyName={addressesFor?.nome}
        onClose={() => setAddressesFor(null)}
      />
    </>
  );
}
